import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Assignment, Edit, Delete, Add } from '@mui/icons-material';

const Assignments = () => {
  const [assignments, setAssignments] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [formData, setFormData] = useState({
    leadIds: [],
    assignedTo: '',
    reason: '',
  });

  useEffect(() => {
    fetchAssignments();
  }, []);

  const fetchAssignments = async () => {
    // Mock data - replace with actual API call
    setAssignments([
      {
        id: 1,
        leadName: 'John Doe',
        assignedTo: 'Jane Smith',
        assignedBy: 'Manager',
        reason: 'Expertise in diabetes cases',
        assignedAt: '2024-01-15T10:30:00Z',
        status: 'active',
      },
      {
        id: 2,
        leadName: 'Mary Johnson',
        assignedTo: 'Mike Wilson',
        assignedBy: 'Team Lead',
        reason: 'Geographic proximity',
        assignedAt: '2024-01-14T15:45:00Z',
        status: 'completed',
      },
    ]);
  };

  const handleCreateAssignment = () => {
    setSelectedAssignment(null);
    setFormData({ leadIds: [], assignedTo: '', reason: '' });
    setDialogOpen(true);
  };

  const handleEditAssignment = (assignment) => {
    setSelectedAssignment(assignment);
    setFormData({
      leadIds: [assignment.leadId],
      assignedTo: assignment.assignedTo,
      reason: assignment.reason,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (selectedAssignment) {
        // Update assignment
        console.log('Updating assignment:', formData);
      } else {
        // Create new assignment
        console.log('Creating assignment:', formData);
      }
      setDialogOpen(false);
      fetchAssignments();
    } catch (error) {
      console.error('Failed to save assignment:', error);
    }
  };

  const handleDeleteAssignment = async (id) => {
    if (window.confirm('Are you sure you want to delete this assignment?')) {
      try {
        console.log('Deleting assignment:', id);
        fetchAssignments();
      } catch (error) {
        console.error('Failed to delete assignment:', error);
      }
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Lead Assignments
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreateAssignment}
        >
          New Assignment
        </Button>
      </Box>

      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Lead Name</TableCell>
                  <TableCell>Assigned To</TableCell>
                  <TableCell>Assigned By</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assignments.map((assignment) => (
                  <TableRow key={assignment.id}>
                    <TableCell>{assignment.leadName}</TableCell>
                    <TableCell>{assignment.assignedTo}</TableCell>
                    <TableCell>{assignment.assignedBy}</TableCell>
                    <TableCell>{assignment.reason}</TableCell>
                    <TableCell>
                      {new Date(assignment.assignedAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={assignment.status}
                        color={assignment.status === 'active' ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleEditAssignment(assignment)}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteAssignment(assignment.id)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Assignment Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedAssignment ? 'Edit Assignment' : 'New Assignment'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Assign To</InputLabel>
              <Select
                value={formData.assignedTo}
                onChange={(e) => setFormData({ ...formData, assignedTo: e.target.value })}
                label="Assign To"
              >
                <MenuItem value="Jane Smith">Jane Smith (BDA)</MenuItem>
                <MenuItem value="Mike Wilson">Mike Wilson (BDA)</MenuItem>
                <MenuItem value="Sarah Davis">Sarah Davis (BDA)</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Reason for Assignment"
              multiline
              rows={3}
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              fullWidth
              placeholder="Why are you making this assignment?"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.assignedTo || !formData.reason}
          >
            {selectedAssignment ? 'Update' : 'Create'} Assignment
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Assignments;